import type {
  BudgetDocument,
  BudgetLine,
  BudgetValidationIssue,
  BudgetValidationResult,
} from '../types'
import { isProductLike } from '../models'

export class BudgetValidator {
  validate(document: BudgetDocument): BudgetValidationResult {
    const issues: BudgetValidationIssue[] = []

    if (!document.title.trim()) {
      issues.push({
        severity: 'warning',
        code: 'BUDGET_TITLE_EMPTY',
        message: 'Título do orçamento está vazio',
        path: 'title',
      })
    }

    if (!document.client.name.trim()) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_CLIENT_REQUIRED',
        message: 'Cliente é obrigatório',
        path: 'client.name',
      })
    }

    if (document.lines.length === 0) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_LINES_EMPTY',
        message: 'Orçamento sem itens',
        path: 'lines',
      })
    }

    for (const line of document.lines) {
      issues.push(...this.validateLine(line))
    }

    if (document.globalDiscountPercent < 0 || document.globalDiscountPercent > 100) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_DISCOUNT_PERCENT_RANGE',
        message: 'Desconto percentual global deve estar entre 0 e 100',
        path: 'globalDiscountPercent',
      })
    }

    if (document.shipping < 0) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_SHIPPING_NEGATIVE',
        message: 'Frete não pode ser negativo',
        path: 'shipping',
      })
    }

    if (document.taxRatePercent < 0 || document.taxRatePercent > 100) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_TAX_RATE_RANGE',
        message: 'Alíquota de imposto deve estar entre 0 e 100',
        path: 'taxRatePercent',
      })
    }

    if (document.totals.total < 0) {
      issues.push({
        severity: 'error',
        code: 'BUDGET_TOTAL_NEGATIVE',
        message: 'Total do orçamento não pode ser negativo',
        path: 'totals.total',
      })
    }

    const hasError = issues.some((issue) => issue.severity === 'error')
    return { valid: !hasError, issues }
  }

  validateLine(line: BudgetLine): BudgetValidationIssue[] {
    const issues: BudgetValidationIssue[] = []

    if (!line.description.trim()) {
      issues.push({
        severity: 'error',
        code: 'LINE_DESCRIPTION_REQUIRED',
        message: 'Descrição do item é obrigatória',
        lineId: line.id,
        path: 'description',
      })
    }

    if (line.quantity <= 0 && isProductLike(line.type)) {
      issues.push({
        severity: 'error',
        code: 'LINE_QUANTITY_INVALID',
        message: 'Quantidade deve ser maior que zero',
        lineId: line.id,
        path: 'quantity',
      })
    }

    if (line.unitPrice < 0 || line.unitCost < 0) {
      issues.push({
        severity: 'error',
        code: 'LINE_PRICE_NEGATIVE',
        message: 'Custo e preço unitários não podem ser negativos',
        lineId: line.id,
        path: 'unitPrice',
      })
    }

    if (
      line.type === 'product' &&
      line.origin.source !== 'manual' &&
      !line.origin.productId
    ) {
      issues.push({
        severity: 'warning',
        code: 'LINE_PRODUCT_ID_MISSING',
        message: 'Produto sem productId — rastreabilidade incompleta',
        lineId: line.id,
        path: 'origin.productId',
      })
    }

    return issues
  }
}

export const budgetValidator = new BudgetValidator()
